import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, ExternalLink } from "lucide-react";

const projects = [
  {
    id: 1,
    title: "Punjab Financial & Insurance Services",
    category: "Social Media Marketing",
    image: "https://images.unsplash.com/photo-1547658719-da2b51169166",
    description:
      "Built a complete content direction and digital foundation for a growing insurance brand.",
    tags: ["Content Direction", "Brand Foundation", "Marketing"],
    path: "/services/social-media-marketing",
  },
  {
    id: 2,
    title: "Canada RDSP + Punjab RDSP",
    category: "Custom Tool Creation",
    image: "https://images.unsplash.com/photo-1593720213428-28a5b9e94613",
    description:
      "Built custom RDSP calculators to help insurance agents calculate grants and bonds more clearly.",
    tags: ["RDSP Calculator", "Custom Tool", "Insurance"],
    path: "/services/custom-tool-creation",
  },
  {
    id: 3,
    title: "Big Boys Motor Club",
    category: "Custom Tool Creation",
    image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085",
    description:
      "Built a custom admin dashboard to help manage listings and inventory more efficiently.",
    tags: ["Admin Dashboard", "Inventory", "Custom Tool"],
    path: "/services/custom-tool-creation",
  },
];

export const ProjectsSection = () => {
  return (
    <section
      id="featured-work"
      className="section-padding bg-foreground text-background relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-primary/10 to-transparent" />
        <div className="absolute -bottom-24 right-0 w-80 h-80 sm:w-96 sm:h-96 bg-secondary/10 rounded-full blur-3xl" />
      </div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
        >
          <span className="text-sm font-medium text-secondary uppercase tracking-wider">
            Our Work
          </span>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold mt-3 mb-4 sm:mb-6">
            Real Problems. Practical{" "}
            <span className="gradient-text">Solutions.</span>
          </h2>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: index * 0.08 }}
              className="h-full"
            >
              <div className="group relative bg-background/5 rounded-2xl overflow-hidden border border-background/10 hover:border-primary/30 transition-all duration-500 h-full">
                {/* Image */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/55 to-transparent opacity-70 group-hover:opacity-85 transition-opacity" />

                  {/* Overlay Content */}
                  <div className="absolute inset-0 p-5 sm:p-6 flex flex-col justify-end items-center text-center sm:items-start sm:text-left">
                    <Badge className="w-fit mb-3 bg-primary/20 text-primary-foreground border-none mx-auto sm:mx-0">
                      {project.category}
                    </Badge>

                    <h3 className="text-xl font-display font-bold text-background mb-2">
                      {project.title}
                    </h3>

                    <p className="text-sm sm:text-base text-background/70 mb-4 leading-relaxed">
                      {project.description}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                      {project.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="text-xs text-background/70 bg-background/10 px-2 py-1 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Hover Link (desktop hover) */}
                <Link
                  to={project.path}
                  className="absolute inset-0 hidden sm:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-primary/90"
                >
                  <span className="flex items-center gap-2 text-primary-foreground font-medium">
                    View Project
                    <ExternalLink className="h-4 w-4" />
                  </span>
                </Link>

                {/* Mobile bottom action */}
                <div className="sm:hidden p-4 border-t border-background/10 flex justify-center">
                  <Link
                    to={project.path}
                    className="inline-flex items-center gap-2 text-sm font-medium text-secondary"
                  >
                    View Project <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mt-10 sm:mt-14"
        >
          <Button
            variant="outline"
            size="lg"
            asChild
            className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:bg-white/10 hover:text-white hover:border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
          >
            <Link to="/contact" className="flex items-center justify-center gap-2">
              View Our Work
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};